import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Maximize, Gauge, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

// Cloudflare Stream player using hls.js with seamless background token refresh.
// Paid videos use short-lived (15 min) signed tokens; a custom loader swaps in a
// freshly issued token before expiry so playback never stops mid-video.
export function CloudflarePlayer({ lessonId, source, startAt = 0, onProgress, onEnded }) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const tokenRef = useRef(source?.token || null);
  const hlsRef = useRef(null);
  const refreshTimer = useRef(null);
  const [speed, setSpeed] = useState(1);
  const [showSpeed, setShowSpeed] = useState(false);
  const [error, setError] = useState(null);
  const [fallbackIframe, setFallbackIframe] = useState(null);

  const signed = source?.mode === "signed";

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !source?.hls_url) return;
    let destroyed = false;

    const scheduleRefresh = (ttl) => {
      clearTimeout(refreshTimer.current);
      const base = Number(ttl) || 900;
      const lead = Math.min(120, Math.floor(base * 0.5));
      const ms = Math.max(base - lead, 5) * 1000;
      refreshTimer.current = setTimeout(async () => {
        try {
          const { data } = await api.get(`/lessons/${lessonId}/token`);
          if (destroyed) return;
          if (data.token) {
            tokenRef.current = data.token; // custom loader picks this up on next segment
            scheduleRefresh(data.ttl || 900);
          }
        } catch (e) {
          // Access revoked or session ended -> stop playback, keep content protected
          if (!destroyed) { video.pause(); setError("Your viewing session ended. Refresh the page to continue."); }
        }
      }, ms);
    };

    const nativeHls = video.canPlayType("application/vnd.apple.mpegurl");

    if (Hls.isSupported() && !nativeHls) {
      // Custom loader rewrites the token segment of every request URL to the latest token
      class TokenLoader extends Hls.DefaultConfig.loader {
        load(context, config, callbacks) {
          if (context?.url && tokenRef.current) {
            context.url = context.url.replace(
              /(\/(?:videodelivery\.net|[^/]+\.cloudflarestream\.com)\/)[^/]+(\/)/,
              `$1${tokenRef.current}$2`,
            );
          }
          super.load(context, config, callbacks);
        }
      }
      const hls = new Hls({ loader: TokenLoader, maxBufferLength: 30, enableWorker: true });
      hlsRef.current = hls;
      hls.loadSource(source.hls_url);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (startAt > 0) { try { video.currentTime = startAt; } catch (e) {} }
      });
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) {
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
          else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
        }
      });
      if (signed) scheduleRefresh(source.ttl || 900);
    } else if (nativeHls) {
      // Safari / iOS: native HLS can't rewrite segment URLs, so request a session-length token once
      const setup = async () => {
        let url = source.hls_url;
        if (signed) {
          try {
            const { data } = await api.get(`/lessons/${lessonId}/token`, { params: { long: 1 } });
            if (data.hls_url) url = data.hls_url;
          } catch (e) {}
        }
        if (destroyed) return;
        video.src = url;
        const onLoaded = () => { if (startAt > 0) { try { video.currentTime = startAt; } catch (e) {} } };
        video.addEventListener("loadedmetadata", onLoaded, { once: true });
      };
      setup();
    } else if (source.iframe_url) {
      setFallbackIframe(source.iframe_url);
    }

    return () => {
      destroyed = true;
      clearTimeout(refreshTimer.current);
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, source?.hls_url]);

  useEffect(() => { if (videoRef.current) videoRef.current.playbackRate = speed; }, [speed]);

  const goFull = () => {
    const el = wrapRef.current;
    if (el?.requestFullscreen) el.requestFullscreen();
    else if (videoRef.current?.webkitEnterFullscreen) videoRef.current.webkitEnterFullscreen();
  };

  const aspect = "aspect-[9/16] w-full max-w-[420px] max-h-[80vh] mx-auto";

  if (fallbackIframe) {
    return (
      <div className={`relative overflow-hidden rounded-xl border border-white/10 bg-black gold-glow ${aspect}`}>
        <iframe data-testid="video-iframe" title="lesson" src={fallbackIframe}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen className="absolute inset-0 h-full w-full" style={{ border: 0 }} />
      </div>
    );
  }

  return (
    <div ref={wrapRef} className={`relative overflow-hidden rounded-xl border border-white/10 bg-black gold-glow ${aspect}`}>
      <video
        ref={videoRef}
        data-testid="video-element"
        controls
        playsInline
        controlsList="nodownload"
        onContextMenu={(e) => e.preventDefault()}
        className="h-full w-full bg-black"
        onTimeUpdate={(e) => onProgress && onProgress(e.target.currentTime)}
        onEnded={() => onEnded && onEnded()}
      />
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/85 p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-[#DCA846]" />
          <p className="text-sm text-zinc-200">{error}</p>
        </div>
      )}
      <div className="absolute top-3 right-3 flex gap-2">
        <div className="relative">
          <button data-testid="speed-btn" onClick={() => setShowSpeed((s) => !s)}
            className="flex items-center gap-1 rounded bg-black/70 px-2 py-1 text-xs text-white border border-white/10">
            <Gauge className="h-3.5 w-3.5" /> {speed}x
          </button>
          {showSpeed && (
            <div className="absolute right-0 mt-1 rounded bg-black/90 border border-white/10 p-1">
              {SPEEDS.map((s) => (
                <button key={s} onClick={() => { setSpeed(s); setShowSpeed(false); }}
                  className={`block w-16 rounded px-2 py-1 text-left text-xs ${s === speed ? "text-[#DCA846]" : "text-white"} hover:bg-white/10`}>
                  {s}x
                </button>
              ))}
            </div>
          )}
        </div>
        <button data-testid="fullscreen-btn" onClick={goFull} className="rounded bg-black/70 p-1.5 text-white border border-white/10">
          <Maximize className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
